import { useState, useCallback, useEffect, useRef } from "react";

export const useAgentSearch = () => {
  // Agent search state
  const [agentOptions, setAgentOptions] = useState([]);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedAgentIndex, setSelectedAgentIndex] = useState(-1);
  const agentInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Agent search functions
  const fetchDefaultAgents = useCallback(async () => {
    try {
      setLoadingAgents(true);
      const token = localStorage.getItem('token') || '';
      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const response = await fetch(`${baseUrl}/agents/?limit=5&no_pagination=true`, {
        headers: {
          "Authorization": `Token ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch agents");
      }

      const data = await response.json();
      setAgentOptions(data.results || data || []);
    } catch (error) {
      console.error("Failed to fetch default agents:", error);
      setAgentOptions([]);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  const searchAgents = useCallback(async (searchTerm) => {
    try {
      setLoadingAgents(true);
      const token = localStorage.getItem('token') || '';
      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      
      const response = await fetch(
        `${baseUrl}/agents/?search=${encodeURIComponent(searchTerm)}&no_pagination=true`,
        {
          headers: {
            "Authorization": `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (!response.ok) {
        throw new Error("Failed to search agents");
      }
      
      const data = await response.json();
      setAgentOptions(data.results || data || []);
    } catch (error) {
      console.error("Failed to search agents:", error);
      setAgentOptions([]);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  const handleAgentFocus = () => {
    setShowAgentDropdown(true);
  };

  const handleAgentKeyDown = (e, agentValue, onAgentSelect) => {
    if (!showAgentDropdown || agentOptions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedAgentIndex(prev => 
          prev < agentOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedAgentIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedAgentIndex >= 0) {
          onAgentSelect(agentOptions[selectedAgentIndex]);
        }
        break;
      case 'Escape':
        setShowAgentDropdown(false);
        setSelectedAgentIndex(-1);
        agentInputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleAgentSelect = (agent, onInputChange) => {
    const agentDisplayName = agent.name || agent.full_name || agent.first_name + ' ' + agent.last_name || '';
    onInputChange("agent", agentDisplayName);
    onInputChange("agentId", agent.id);

    setShowAgentDropdown(false);
    setSelectedAgentIndex(-1);
  };

  const handleAgentChange = (value, onInputChange) => {
    onInputChange("agent", value);
    onInputChange("agentId", ""); // Clear agent ID when typing manually
    setSelectedAgentIndex(-1);
    
    if (value.length >= 1) {
      searchAgents(value);
    } else {
      fetchDefaultAgents();
    }
  };

  const handleClearAgent = (onInputChange) => {
    onInputChange("agent", "");
    onInputChange("agentId", "");
    setSelectedAgentIndex(-1);
    fetchDefaultAgents();
    agentInputRef.current?.focus();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAgentDropdown(false);
        setSelectedAgentIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listen for agent updates from other tabs
  useEffect(() => {
    let channel;

    try {
      channel = new BroadcastChannel('agent_updates');

      channel.onmessage = (event) => {
        if (event.data.type === 'AGENT_CREATED') {
          // Refresh agent list when a new agent is created in another tab
          fetchDefaultAgents();
        }
      };
    } catch (error) {
      console.error('Failed to setup BroadcastChannel:', error);
    }

    // Backup mechanism: refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const lastUpdate = localStorage.getItem('agent_last_update');
        if (lastUpdate) {
          const timeDiff = Date.now() - parseInt(lastUpdate, 10);
          // Refresh if update happened in last 5 minutes
          if (timeDiff < 5 * 60 * 1000) {
            fetchDefaultAgents();
            localStorage.removeItem('agent_last_update');
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (channel) {
        channel.close();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchDefaultAgents]);

  return {
    // State
    agentOptions,
    showAgentDropdown,
    loadingAgents,
    selectedAgentIndex,
    agentInputRef,
    dropdownRef,
    // Actions
    fetchDefaultAgents,
    searchAgents,
    handleAgentFocus,
    handleAgentKeyDown,
    handleAgentSelect,
    handleAgentChange,
    handleClearAgent,
    setSelectedAgentIndex,
  };
};